import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";

interface Permission {
  id: number;
  name: string;
  codename: string;
  content_type: {
    id: number;
    app_label: string;
    model: string;
  };
  title: string | null;
}

interface GroupedPermissions {
  [key: string]: Permission[];
}

interface FormValues {
  roleName: string;
  permissions: number[];
}

const EditAddRole = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppThunkDispatch>();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [permissionsList, setPermissionsList] = useState<GroupedPermissions>(
    {},
  );
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  const { t } = useTranslation("rolesAndPermissions");
  const [submitType, setSubmitType] = useState<"save" | "save_new">("save");
  const [loadingButton, setLoadingButton] = useState<
    "save" | "save_new" | null
  >(null);

  // Validation schema
  const validationSchema = Yup.object({
    roleName: Yup.string()
      .required(t("editAdd.validation.roleName.required"))
      .max(50, t("editAdd.validation.roleName.maxLength"))
      .trim()
      .matches(/^[a-zA-Z0-9\s-_]+$/, t("editAdd.validation.roleName.format")),
    permissions: Yup.array()
      .min(1, t("editAdd.validation.permissions.min"))
      .required(t("editAdd.validation.permissions.required")),
  });

  // Fetch permissions list
  useEffect(() => {
    const fetchPermissions = async () => {
      setIsLoadingPermissions(true);
      try {
        const { authenticatedCall } = actionsHandler("permissionsList");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
          }),
        );

        setPermissionsList(response.data.data);
      } catch (error) {
        toast.error(t("editAdd.messages.error.loadPermissions"));
      } finally {
        setIsLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, [dispatch, t]);

  // Fetch role details if editing
  useEffect(() => {
    const fetchRoleDetails = async () => {
      if (!id) return;

      try {
        const { authenticatedCall } = actionsHandler("roleWithoutSlash");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customParams: [id + "/"],
          }),
        );
        formik.setValues({
          roleName: response.data.name,
          permissions: response.data.permissions.map((p: Permission) => p.id),
        });
      } catch (error) {
        toast.error(t("editAdd.messages.error.loadRoleDetails"));
        navigate("/roles-and-permissions");
      }
    };

    if (id) {
      fetchRoleDetails();
    }
  }, [id, dispatch, navigate, t]);

  const handleSubmit = async (values: FormValues) => {
    try {
      setLoadingButton(submitType);
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler(
        id ? "roleWithoutSlash" : "roles",
      );

      const payload = {
        name: values.roleName,
        permission_ids: values.permissions,
      };

      const response = await dispatch(
        authenticatedCall({
          method: id ? "put" : "post",
          customParams: id ? [id + "/"] : undefined,
          payload,
        }),
      );

      if (response.ok) {
        toast.success(
          response.data?.message ||
            t(
              id
                ? "editAdd.messages.success.update"
                : "editAdd.messages.success.create",
            ),
        );
        if (submitType === "save_new") {
          formik.resetForm();
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        } else {
          navigate("/roles-and-permissions");
        }
      }
    } catch (error: any) {
      console.log(error);
      const errorMessage =
        error.response?.data?.message || t("editAdd.messages.error.create");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
      setLoadingButton(null);
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      roleName: "",
      permissions: [],
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSelectAllForGroup = (title: string, isSelected: boolean) => {
    const groupPermissions = permissionsList[title];
    const permissionIds = groupPermissions.map((p) => p.id);

    let newPermissions = [...formik.values.permissions];
    if (isSelected) {
      newPermissions = [...new Set([...newPermissions, ...permissionIds])];
    } else {
      newPermissions = newPermissions.filter(
        (id) => !permissionIds.includes(id),
      );
    }

    formik.setFieldValue("permissions", newPermissions);
  };

  // Add the skeleton component
  const PermissionsSkeleton = () => (
    <>
      {[1, 2, 3].map((group) => (
        <div key={group} className="tw-mb-6">
          <div className="inline-custom-card">
            <div className="inline-custom-card-header">
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                <Skeleton variant="text" width={150} height={24} />
                <Skeleton variant="rectangular" width={100} height={24} />
              </div>
            </div>
            <div className="inline-custom-card-body">
              <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-4 md:tw-gap-2 tw-gap-1">
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <div
                    key={item}
                    className="tw-flex tw-items-center tw-space-x-3"
                  >
                    <Skeleton variant="rectangular" width={20} height={20} />
                    <Skeleton variant="text" width={120} height={24} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <img
                src={BackIcon}
                alt="Back Icon"
                className="tw-cursor-pointer"
                onClick={() => navigate("/roles-and-permissions")}
              />
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold">
                {t(`editAdd.title.${id ? "edit" : "add"}`)}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="tw-px-8 tw-py-4 tw-mt-[140px]">
        <div className="tw-bg-white custom-card">
          <div>
            <div className="custom-card-header">
              <div className="tw-py-3">
                <div className="form-group tw-mb-6 sm:tw-w-[380px] tw-w-full">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    {t("editAdd.form.roleName.label")}
                  </p>
                  <input
                    type="text"
                    name="roleName"
                    placeholder={t("editAdd.form.roleName.placeholder")}
                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md"
                    value={formik.values.roleName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.roleName && formik.errors.roleName && (
                    <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                      {formik.errors.roleName}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="custom-card-body">
              {/* Permissions */}
              <div className="tw-mt-6">
                <h3 className="font-size-base tw-font-semibold font-primary-color tw-mb-4">
                  {t("editAdd.form.permissions.title")}
                </h3>

                {isLoadingPermissions ? (
                  <PermissionsSkeleton />
                ) : (
                  Object.entries(permissionsList).map(
                    ([title, permissions]) => (
                      <div key={title} className="tw-mb-6">
                        <div className="inline-custom-card">
                          <div className="inline-custom-card-header">
                            <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                              <h4 className="font-gray-color-dark font-weight-medium">
                                {title}
                              </h4>
                              <label className="tw-flex tw-items-center tw-space-x-3">
                                <input
                                  type="checkbox"
                                  onChange={(e) =>
                                    handleSelectAllForGroup(
                                      title,
                                      e.target.checked,
                                    )
                                  }
                                  checked={permissions.every((p) =>
                                    formik.values.permissions.includes(p.id),
                                  )}
                                  className="tw-w-4 tw-h-4 tw-rounded tw-border-gray-300"
                                />
                                <span className="tw-text-sm font-gray-color-dark">
                                  {t("editAdd.form.permissions.selectAll")}
                                </span>
                              </label>
                            </div>
                          </div>
                          <div className="inline-custom-card-body">
                            <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-4 md:tw-gap-2 tw-gap-1">
                              {permissions.map((permission) => (
                                <label
                                  key={permission.id}
                                  className="tw-flex tw-items-center tw-space-x-3"
                                >
                                  <input
                                    type="checkbox"
                                    checked={formik.values.permissions.includes(
                                      permission.id,
                                    )}
                                    onChange={() => {
                                      const newPermissions =
                                        formik.values.permissions.includes(
                                          permission.id,
                                        )
                                          ? formik.values.permissions.filter(
                                              (id) => id !== permission.id,
                                            )
                                          : [
                                              ...formik.values.permissions,
                                              permission.id,
                                            ];
                                      formik.setFieldValue(
                                        "permissions",
                                        newPermissions,
                                      );
                                    }}
                                    className="tw-w-4 tw-h-4 tw-rounded tw-border-gray-300"
                                  />
                                  <span className="tw-text-sm font-gray-color-dark">
                                    {permission.name}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ),
                  )
                )}
              </div>

              {/* Permissions Validation Message */}
              {formik.touched.permissions && formik.errors.permissions && (
                <div className="tw-text-red-500 tw-text-sm tw-mt-2 tw-mb-4">
                  <span className="tw-flex tw-items-center">
                    <svg
                      className="tw-w-4 tw-h-4 tw-mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {formik.errors.permissions}
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="tw-flex tw-justify-center tw-space-x-4 tw-mt-6">
                <button
                  type="button"
                  onClick={() => navigate("/roles-and-permissions")}
                  className="tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50"
                >
                  {t("editAdd.form.buttons.cancel")}
                </button>
                {id ? (
                  // Edit mode - single update button
                  <button
                    type="submit"
                    onClick={() => setSubmitType("save")}
                    disabled={isLoading || formik.isSubmitting}
                    className={`primary-custom-button tw-px-4 tw-py-2 tw-text-white tw-rounded-md ${
                      loadingButton === "save"
                        ? "tw-bg-blue-400 tw-cursor-not-allowed"
                        : "tw-bg-blue-600 hover:tw-bg-blue-700"
                    }`}
                  >
                    {loadingButton === "save"
                      ? t("editAdd.form.buttons.saving")
                      : t("editAdd.form.buttons.update")}
                  </button>
                ) : (
                  // Add mode - save and save_new buttons
                  <>
                    <button
                      type="submit"
                      onClick={() => setSubmitType("save")}
                      disabled={isLoading || formik.isSubmitting}
                      className={`primary-custom-button tw-px-4 tw-py-2 tw-text-white tw-rounded-md ${
                        loadingButton === "save"
                          ? "tw-bg-blue-400 tw-cursor-not-allowed"
                          : "tw-bg-blue-600 hover:tw-bg-blue-700"
                      }`}
                    >
                      {loadingButton === "save"
                        ? t("editAdd.form.buttons.saving")
                        : t("editAdd.form.buttons.save")}
                    </button>
                    <button
                      type="submit"
                      onClick={() => setSubmitType("save_new")}
                      disabled={isLoading || formik.isSubmitting}
                      className={`primary-custom-button tw-px-4 tw-py-2 tw-text-white tw-rounded-md ${
                        loadingButton === "save_new"
                          ? "tw-bg-blue-400 tw-cursor-not-allowed"
                          : "tw-bg-blue-600 hover:tw-bg-blue-700"
                      }`}
                    >
                      {loadingButton === "save_new"
                        ? t("editAdd.form.buttons.saving")
                        : t("editAdd.form.buttons.save_new")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default EditAddRole;
